import styled, { css } from "../../__compat__/styled-components/main.entry.mjs.mjs";
import { injectSpacing } from "../../api/spacing/injectSpacing.mjs.mjs";
// All values are pixel values, units are omitted for calculations.
export const containerWidth = 58;
export const containerHeight = 32;
const containerPadding = 3;
const switchIdleSize = 26;
const switchHoverSize = 20;
const switchHoverScaleFactor = switchHoverSize / switchIdleSize;
const hoverEffectDuration = '150ms';
const toggleEffectDuration = '250ms';
export const StyledContainer = styled.div.withConfig({ displayName: "auir-611444a-StyledContainer", componentId: "auir-611444a-4apj4n" }) `
  ${injectSpacing('margin')}

  display: flex;
  align-items: flex-start;
`;
export const StyledToggleContainer = styled.span.withConfig({ displayName: "auir-611444a-StyledToggleContainer", componentId: "auir-611444a-1vg194" }) `
  position: relative;

  display: grid;
  flex-shrink: 0;
  grid-template-columns: repeat(2, 1fr);

  box-sizing: border-box;

  width: ${containerWidth + 'px'};
  height: ${containerHeight + 'px'};
`;
export const StyledToggleButton = styled.button.withConfig({ displayName: "auir-611444a-StyledToggleButton", componentId: "auir-611444a-z5hi2e" }) `
  grid-row: 1;
  grid-column: 1 / 3;

  margin: 0;
  padding: 0;

  background-color: var(
    ${(props) => props.theme.colors.interaction.tertiary.default}
  );

  border: none;

  border-radius: ${containerHeight / 2 + 'px'};
  outline-offset: var(${(props) => props.theme.responsive.spacing.xxs});

  cursor: pointer;

  transition-timing-function: var(${(props) => props.theme.easing});
  transition-duration: ${toggleEffectDuration};
  transition-property: background-color;

  appearance: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: var(
      ${(props) => props.theme.colors.interaction.tertiary.hover}
    );
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &[aria-checked='true'] {
    background-color: var(${(props) => props.theme.colors.ui.success});
  }
`;
export const StyledToggleSwitch = styled.div.withConfig({ displayName: "auir-611444a-StyledToggleSwitch", componentId: "auir-611444a-1onppip" }) `
  position: relative;

  grid-row: 1;
  grid-column: 1 / 3;

  pointer-events: none;

  &::after {
    position: absolute;

    top: ${containerPadding + 'px'};

    width: ${switchIdleSize + 'px'};
    height: ${switchIdleSize + 'px'};

    background-color: var(${(props) => props.theme.colors.base.grey[0]});

    border-radius: 50%;
    transform-origin: center;

    transition-timing-function: var(${(props) => props.theme.easing});
    transition-duration: ${toggleEffectDuration};
    transition-property: left, transform, translate, scale;

    content: '';
  }

  ${StyledToggleButton}:disabled ~ & {
    opacity: 0.7;
  }

  /**
   * This is the fallback for browsers not supporting dedicated transform
   * properties
   */
  @supports not ((translate: 0px) or (scale: 1)) {
    ${StyledToggleButton}[aria-checked='false'] ~ &::after,
  [dir='rtl'] ${StyledToggleButton}[aria-checked='true'] ~ &::after {
      left: ${containerPadding + 'px'};
    }

    ${StyledToggleButton}[aria-checked='true'] ~ &::after,
  [dir='rtl'] ${StyledToggleButton}[aria-checked='false'] ~ &::after {
      left: ${containerWidth - switchIdleSize - containerPadding + 'px'};
    }

    ${StyledToggleButton}:not(:disabled):hover ~ &::after {
      transform: scale(${switchHoverScaleFactor});
    }
  }

  /**
   * This takes advantage of the new dedicated transform properties and is a
   * future proof solution. Even though it was primarely introduced to fix a bug
   * in iOS 15, where transition had no effect on 'left' property
   */
  @supports (translate: 0px) or (scale: 1) {
    ${StyledToggleButton}[aria-checked='false'] ~ &::after,
    [dir='rtl'] ${StyledToggleButton}[aria-checked='true'] ~ &::after {
      translate: ${containerPadding + 'px'} 0;
    }

    ${StyledToggleButton}[aria-checked='true'] ~ &::after,
    [dir='rtl'] ${StyledToggleButton}[aria-checked='false'] ~ &::after {
      translate: ${containerWidth - switchIdleSize - containerPadding + 'px'} 0;
    }

    ${StyledToggleButton}:not(:disabled):hover ~ &::after {
      scale: ${switchHoverScaleFactor};
    }
  }
`;
export const StyledOnIndicator = styled.div.withConfig({ displayName: "auir-611444a-StyledOnIndicator", componentId: "auir-611444a-1jhy4k3" }) `
  grid-row: 1;
  grid-column: 1;

  align-self: center;
  justify-self: center;

  margin-inline-start: ${containerPadding + 'px'};

  width: 1px;
  height: 8px;

  background: var(${(props) => props.theme.colors.base.grey[0]});

  transition-timing-function: var(${(props) => props.theme.easing});
  transition-duration: ${toggleEffectDuration}, ${hoverEffectDuration};
  transition-property: transform, opacity;

  ${StyledToggleButton}:disabled ~ & {
    opacity: 0;
  }

  pointer-events: none;

  ${StyledToggleButton}[aria-checked='false'] ~ & {
    transform: translateX(${switchIdleSize + 'px'});
    opacity: 0;
  }

  [dir='rtl'] ${StyledToggleButton}[aria-checked='false'] ~ & {
    transform: translateX(${switchIdleSize * -1 + 'px'});
  }
`;
export const StyledOffIndicator = styled.div.withConfig({ displayName: "auir-611444a-StyledOffIndicator", componentId: "auir-611444a-1hanbgg" }) `
  grid-row: 1;
  grid-column: 2;

  align-self: center;
  justify-self: center;

  box-sizing: border-box;

  width: 6px;
  height: 6px;

  margin-inline-end: ${containerPadding + 'px'};

  border: solid 1px var(${(props) => props.theme.colors.ui.tertiary});
  border-radius: 50%;

  transition-timing-function: var(${(props) => props.theme.easing});
  transition-duration: ${toggleEffectDuration}, ${hoverEffectDuration};
  transition-property: transform, opacity;

  pointer-events: none;

  ${StyledToggleButton}:disabled ~ & {
    opacity: 0;
  }

  ${StyledToggleButton}[aria-checked='true'] ~ & {
    transform: translateX(${switchIdleSize * -1 + 'px'});
    opacity: 0;
  }

  [dir='rtl'] ${StyledToggleButton}[aria-checked='true'] ~ & {
    transform: translateX(${switchIdleSize + 'px'});
  }
`;
export const StyledLabelContainer = styled.div.withConfig({ displayName: "auir-611444a-StyledLabelContainer", componentId: "auir-611444a-1jcprw2" }) `
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: var(${(props) => props.theme.responsive.spacing.s});
  flex-grow: 1;
  ${(props) => props.labelPosition === 'left' ? 'flex-grow: 0;' : 'flex-grow: initial;'}

  ${(props) => props.labelPosition === 'right' ? 'flex-grow: 1;' : 'flex-grow: initial;'}

padding-inline-start: var(${(props) => props.theme.responsive.spacing.s});
  ${(props) => props.labelPosition === 'right' &&
    css `
      padding-inline-start: var(${(props) => props.theme.responsive.spacing.s});
    `}

  ${(props) => props.labelPosition === 'left' &&
    css `
      padding-inline-end: var(${(props) => props.theme.responsive.spacing.s});
      padding-inline-start: 0;
    `}

  padding-top: var(${(props) => props.theme.responsive.spacing.xxs});
`;
export const StyledFormLabelContainer = styled.div.withConfig({ displayName: "auir-611444a-StyledFormLabelContainer", componentId: "auir-611444a-243jl4" }) `
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;

  & > * {
    flex-grow: 1;
  }
`;
//# sourceMappingURL=Toggle.styles.mjs.mjs.map